// src/pages/admin/Users.jsx
import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useToast } from '../../components/common/Toast';
import { userAPI, authAPI } from '../../services/api';
import { Edit2, Trash2, Plus, Loader2 } from 'lucide-react';

export default function Users() {
  const [users,   setUsers]   = useState([]);
  const [roles,   setRoles]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState({ open: false, mode: 'create', user: null });
  const [confirm, setConfirm] = useState({ open: false, id: null });
  const [form,    setForm]    = useState({ name: '', email: '', password: '', phone: '', role_ids: [] });
  const [saving,  setSaving]  = useState(false);
  const { show, ToastComponent } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, rRes] = await Promise.all([userAPI.getAll(), userAPI.getRoles()]);
      setUsers(uRes.data.data);
      setRoles(rRes.data.data);
    } catch { show('Failed to load', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ name: '', email: '', password: '', phone: '', role_ids: [] });
    setModal({ open: true, mode: 'create', user: null });
  };
  const openEdit = (user) => {
    setForm({ name: user.name, phone: user.phone || '', role_ids: user.role_ids || [], is_active: user.is_active });
    setModal({ open: true, mode: 'edit', user });
  };

  const handleRoleToggle = (roleId) => {
    setForm(p => ({
      ...p,
      role_ids: p.role_ids.includes(roleId)
        ? p.role_ids.filter(r => r !== roleId)
        : [...p.role_ids, roleId],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (modal.mode === 'create') {
        await authAPI.register({ ...form });
        show('User created', 'success');
      } else {
        await userAPI.update(modal.user.id, form);
        show('User updated', 'success');
      }
      setModal(p => ({ ...p, open: false }));
      load();
    } catch (err) {
      show(err.response?.data?.message || 'Save failed', 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await userAPI.delete(id); show('User deleted', 'success'); load(); }
    catch { show('Delete failed', 'error'); }
  };

  return (
    <AdminLayout title="Users & Roles">
      {ToastComponent}
      <div className="page-header">
        <div>
          <h2 className="page-title">Users & Roles</h2>
          <p className="page-subtitle">Manage system users and role assignments</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={16} /> Add User
        </button>
      </div>

      <div className="card">
        {loading ? (
          <div className="flex-center" style={{ padding: 64 }}><Loader2 className="spinner" size={32} /></div>
        ) : (
          <div className="table-container" style={{ border: 'none', borderRadius: 0 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Roles</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td><strong>{u.name}</strong></td>
                    <td style={{ color: 'var(--gray-500)' }}>{u.email}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {(u.roles || []).map(r => (
                          <span key={r} className="badge badge-primary" style={{ textTransform: 'capitalize' }}>{r}</span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${u.is_active ? 'badge-success' : 'badge-gray'}`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.8125rem', color: 'var(--gray-400)' }}>
                      {new Date(u.created_on).toLocaleDateString()}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEdit(u)}><Edit2 size={16} /></button>
                        <button className="btn btn-ghost btn-sm btn-icon" style={{ color: 'var(--danger)' }}
                          onClick={() => setConfirm({ open: true, id: u.id })}><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modal.open}
        onClose={() => setModal(p => ({ ...p, open: false }))}
        title={modal.mode === 'create' ? 'Add New User' : 'Edit User'}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModal(p => ({ ...p, open: false }))}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="form-group">
            <label className="form-label required">Full Name</label>
            <input className="form-input" name="name" value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
          </div>
          {modal.mode === 'create' && (
            <>
              <div className="form-group">
                <label className="form-label required">Email</label>
                <input className="form-input" type="email" name="email" value={form.email || ''} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label required">Password</label>
                <input className="form-input" type="password" name="password" value={form.password || ''} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} />
              </div>
            </>
          )}
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-input" name="phone" value={form.phone || ''} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Assign Roles</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
              {roles.map(r => (
                <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.875rem' }}>
                  <input
                    type="checkbox"
                    checked={(form.role_ids || []).includes(r.id)}
                    onChange={() => handleRoleToggle(r.id)}
                  />
                  <span style={{ textTransform: 'capitalize' }}>{r.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={confirm.open}
        onClose={() => setConfirm({ open: false, id: null })}
        onConfirm={() => handleDelete(confirm.id)}
        title="Delete User"
        message="Are you sure you want to deactivate this user? They will lose access to the system."
        danger
      />
    </AdminLayout>
  );
}
